<template>
	<div class="l-banner" :class="'tone-' + tone" role="status">
		<LIcon v-if="icon" :name="icon" :size="14" class="bn-icon" aria-hidden="true" />

		<div class="bn-body">
			<!-- Expandable banners put the title in a real <button> rather than making the
				whole row clickable: a row-level click handler is invisible to the keyboard,
				and nesting the close button inside a clickable row is worse again. -->
			<button v-if="expandable" type="button" class="bn-title bn-title-action" @click="emit('open')">
				<span class="bn-title-text">{{ title }}</span>
				<span class="bn-more">{{ moreLabel }}</span>
			</button>
			<span v-else class="bn-title">{{ title }}</span>

			<div v-if="$slots.default" class="bn-detail"><slot /></div>
		</div>

		<div v-if="$slots.action" class="bn-action"><slot name="action" /></div>

		<button v-if="dismissible" type="button" class="bn-close" :aria-label="dismissLabel" @click="emit('dismiss')">
			<LIcon name="xmark" :size="14" aria-hidden="true" />
		</button>
	</div>
</template>










<script setup>
/**
 * A full-width notice at the top of a page — a maintenance window, an alert, something
 * the reader has to be told before they carry on.
 *
 * Not a toast and not a status chip. A toast is transient and self-dismissing; a chip
 * labels a record. This is standing page furniture that persists until the reader closes
 * it or the thing it is about is over.
 *
 * `tone` comes from a display-value map's `Tone` (see /helpers/display-values.js), never
 * from a raw colour at the call site. Every banner also carries an icon and a text label,
 * so colour is never the sole carrier of meaning (D6, WCAG 1.4.1).
 */
import LIcon from '../base/Icon.vue';

//-- Props
defineProps({
	title: { type: String, required: true },

	// info | warning | critical | maintenance | neutral
	tone: { type: String, default: 'info' },

	// A Font Awesome name (no fa- prefix). Null renders no icon.
	icon: { type: String, default: null },

	// Shows the close button. A banner the reader must keep seeing passes false.
	dismissible: { type: Boolean, default: false },

	// Makes the title a button, for a message with more to read than the one line.
	expandable: { type: Boolean, default: false },

	// The affordance text next to an expandable title.
	moreLabel: { type: String, default: 'Read more' },

	dismissLabel: { type: String, default: 'Dismiss this message' },
});




//-- Emits
const emit = defineEmits(['dismiss', 'open']);
</script>










<style scoped>
.l-banner
{
	display: flex;
	align-items: flex-start;
	gap: var(--sp-3);
	padding: 11px var(--sp-4);
	border: 1px solid transparent;
	border-radius: var(--r);
	font-family: var(--font-ui);
	font-size: var(--text-sm);
}

.bn-icon
{
	flex: none;
	margin-top: 2px;
}

.bn-body
{
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.bn-title
{
	font-weight: 600;
	line-height: 1.45;
	text-align: left;
	color: inherit;
}

/* The button resets to look exactly like the plain title — the affordance is the
   "Read more" text beside it, not a second visual treatment. */
.bn-title-action
{
	display: inline-flex;
	align-items: baseline;
	flex-wrap: wrap;
	gap: var(--sp-2);
	font: inherit;
	font-weight: 600;
	padding: 0;
	border: 0;
	background: none;
	cursor: pointer;
	min-height: var(--target-min);
}

.bn-more
{
	font-size: var(--text-chip);
	font-weight: 600;
	text-decoration: underline;
	white-space: nowrap;
	opacity: 0.85;
}

.bn-title-action:hover .bn-more
{
	opacity: 1;
}

.bn-detail
{
	font-size: var(--text-chip);
	line-height: 1.5;
	opacity: 0.85;
}

.bn-action
{
	flex: none;
	align-self: center;
}

.bn-close
{
	flex: none;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: var(--target-min);
	height: var(--target-min);
	border: 0;
	border-radius: var(--r-sm);
	background: none;
	color: inherit;
	opacity: 0.65;
	cursor: pointer;
}

.bn-close:hover
{
	opacity: 1;
	background: var(--on-emphasis-fill);
}

/* ---- Tones ----
   Tinted fills, mirroring LTonePill so a banner and a pill about the same thing read as
   the same colour. Maintenance uses the brand brass rather than a status hue: planned
   downtime is not a lifecycle state, and it must not read as a failure. */
.tone-info
{
	color: var(--status-unconfirmed-text);
	background: var(--status-unconfirmed-fill);
	border-color: var(--status-unconfirmed-text);
}

.tone-warning
{
	color: var(--status-pending-text);
	background: var(--status-pending-fill);
	border-color: var(--status-pending-text);
}

.tone-critical
{
	color: var(--status-rejected-text);
	background: var(--status-rejected-fill);
	border-color: var(--status-rejected-text);
}

.tone-maintenance
{
	color: var(--accent-text);
	background: var(--accent-fill);
	border-color: var(--accent-edge);
}

.tone-neutral
{
	color: var(--text-2);
	background: var(--surface-sunken);
	border-color: var(--line-strong);
}
</style>
